import { useCallback, useEffect, useState } from 'react';
import { sessionApi, appointmentPath, isNotAvailable } from '../helpers/sessionApi';

// Session notes (contract C / BE-7 as built):
//   GET/POST /api/appointments/<id>/notes/            rows {id, author:{id,name,initials,role}, body, created_at, updated_at, visibility, read_only?}
//   PATCH/DELETE /api/appointments/<id>/notes/<id>/   (author only)
// A legacy Appointment.notes text comes back as {id:"legacy", read_only:true}.
// If the endpoint is missing (404) the hook falls back to the legacy single text field
// (GET/PATCH /api/appointments/<id>/ {notes}) - mode === 'legacy'.
export const useSessionNotes = (appointmentId, { enabled = true } = {}) => {
  const [notes, setNotes] = useState([]);
  const [mode, setMode] = useState('notes'); // notes | legacy
  const [legacyText, setLegacyText] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    if (!appointmentId || !enabled) return;
    setStatus((s) => (s === 'ready' ? s : 'loading'));
    try {
      const data = await sessionApi(appointmentPath(appointmentId, 'notes/'));
      setNotes(Array.isArray(data) ? data : data?.results || []);
      setMode('notes');
      setStatus('ready');
      setError(null);
    } catch (e) {
      if (isNotAvailable(e)) {
        try {
          const appt = await sessionApi(appointmentPath(appointmentId));
          setLegacyText(appt?.notes || '');
          setMode('legacy');
          setStatus('ready');
        } catch (e2) {
          setError(e2);
          setStatus('error');
        }
      } else {
        setError(e);
        setStatus('error');
      }
    }
  }, [appointmentId, enabled]);

  useEffect(() => {
    reload();
  }, [reload]);

  const create = useCallback(
    async (body, visibility = 'clinician') => {
      const note = await sessionApi(appointmentPath(appointmentId, 'notes/'), { method: 'POST', body: { body, visibility } });
      setNotes((list) => [...list, note]);
      return note;
    },
    [appointmentId],
  );

  const update = useCallback(
    async (id, patch) => {
      const note = await sessionApi(appointmentPath(appointmentId, `notes/${id}/`), { method: 'PATCH', body: patch });
      setNotes((list) => list.map((n) => (n.id === id ? { ...n, ...note } : n)));
      return note;
    },
    [appointmentId],
  );

  const remove = useCallback(
    async (id) => {
      await sessionApi(appointmentPath(appointmentId, `notes/${id}/`), { method: 'DELETE' });
      setNotes((list) => list.filter((n) => n.id !== id));
    },
    [appointmentId],
  );

  const saveLegacy = useCallback(
    async (text) => {
      await sessionApi(appointmentPath(appointmentId), { method: 'PATCH', body: { notes: text } });
      setLegacyText(text);
    },
    [appointmentId],
  );

  return { notes, mode, legacyText, status, error, reload, create, update, remove, saveLegacy };
};

export default useSessionNotes;
